<script>
import { mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';
import CreateOverrideModal from './createOverrideModalNew.vue';
import DeleteConfirmationModal from './deleteConfirmationModal.vue';

export default {
  components: {
    CreateOverrideModal,
    DeleteConfirmationModal
  },
  props: {
    scheduleId: {
      type: String,
      default: null,
    }
  },
  data() {
    return {
      isCreatingOverride: false,
      overrideList: [],
      isLoading: false,
      error: null,
      confirmingDeleteId: null,
    };
  },
  computed: {
    newOverrideList() {
      return this.groupByDateAsArray(this.overrideList);
    }
  },
  methods: {
    ...mapActions('appointments', ['fetchScheduleOverrides', 'deleteScheduleOverride']),

    confirmDelete(id) {
      this.handleDeleteOverride(id);
    },

    async handleDeleteOverride(id) {
      try {
        await this.deleteScheduleOverride({
          overrideId: id,
          scheduleId: this.scheduleId
        });
        useAlert(this.$t('BRANCHES.VIEW_OVERRIDES.ALERTS.DELETE_SUCCESS'));
        await this.loadOverrides();
      } catch (err) {
        console.error('Error deleting override:', err);
        useAlert(err.message || this.$t('BRANCHES.VIEW_OVERRIDES.ALERTS.DELETE_ERROR'))
      } finally {
        this.confirmingDeleteId = null;
      }
    },

    resetCreateOverride() {
      this.isCreatingOverride = false;
    },

    async loadOverrides() {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await this.fetchScheduleOverrides(this.scheduleId);
        this.overrideList = Array.isArray(response) ? response : [];
      } catch (err) {
        console.error('Error loading overrides:', err);
        this.error = err.message || this.$t('BRANCHES.VIEW_OVERRIDES.ERROR_LOADING');
        useAlert(this.error);
        this.overrideList = [];
      } finally {
        this.isLoading = false;
      }
    },

    formatTime(timeString) {
      if (!timeString || timeString === this.$t('BRANCHES.VIEW_WORKING_HOURS.SELECT_START_TIME') || timeString === this.$t('BRANCHES.VIEW_WORKING_HOURS.SELECT_END_TIME')) return timeString;

      try {
        const timeParts = timeString.split(':');
        let hours = parseInt(timeParts[0], 10);
        const minutes = timeParts[1];
        const ampm = hours >= 12 ? this.$t('BRANCHES.VIEW_WORKING_HOURS.PM') : this.$t('BRANCHES.VIEW_WORKING_HOURS.AM');

        hours = hours % 12;
        hours = hours ? hours : 12;

        return `${hours}:${minutes} ${ampm}`;
      } catch (e) {
        return timeString;
      }
    },

    groupByDateAsArray(slots) {
      if (!slots || !Array.isArray(slots) || slots.length === 0) {
        return [];
      }

      const grouped = {};

      slots.forEach(slot => {
        if (slot && slot.override_date) {
          const date = slot.override_date;
          if (!grouped[date]) {
            grouped[date] = {
              reason: slot.reason || '',
              slots: []
            };
          }
          grouped[date].slots.push(slot);
        }
      });

      return Object.keys(grouped).map(date => ({
        date,
        reason: grouped[date].reason,
        slots: grouped[date].slots
      }));
    }
  },

  watch: {
    async scheduleId(newVal) {
      if (newVal) {
        await this.loadOverrides();
      } else {
        this.overrideList = [];
      }
    }
  },

  async mounted() {
    await this.loadOverrides();
  }
};
</script>

<template>
  <div class="p-4 bg-white dark:bg-slate-700 rounded-md border-2 border-slate-100 dark:border-slate-50">
    <!-- Create Modal -->
    <div 
      v-if="isCreatingOverride" 
      class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
    >
      <CreateOverrideModal
        :resetCreateOverride="resetCreateOverride" 
        :scheduleId="scheduleId" 
        :loadOverrides="loadOverrides" 
      />
    </div>

    <!-- Delete Modal -->
    <div 
      v-if="confirmingDeleteId" 
      class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
    >
      <!-- TODO: i18n this: this override -->
      <DeleteConfirmationModal
        deleteName="this override" 
        @confirm-delete="confirmDelete(confirmingDeleteId)"
        @close="confirmingDeleteId = null"
      />
    </div>

    <!-- Header -->
    <div class="flex justify-between items-center mb-4">
      <h1 class="text-xl font-semibold text-gray-900 dark:text-gray-100">{{ $t('BRANCHES.VIEW_OVERRIDES.TITLE') }}</h1>
      <woot-button
        variant="smooth"
        color-scheme="primary"
        icon="plus-sign"
        @click="isCreatingOverride = true"
        :disabled="!scheduleId"
      >
        {{ $t('BRANCHES.VIEW_OVERRIDES.CREATE_OVERRIDE') }}
      </woot-button>
    </div> 

    <!-- Loading Spinner -->
    <div v-if="isLoading" class="flex justify-center my-8">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-woot-500"></div>
    </div>

    <!-- Error Message -->
    <div v-else-if="error" class="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 p-4 rounded-lg mb-4">
      <div class="flex items-center mb-2">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
        </svg>
        <span class="font-medium">{{ $t('BRANCHES.VIEW_OVERRIDES.ERROR_LOADING') }}</span>
      </div>
      <p>{{ error }}</p>
      <button 
        @click="loadOverrides" 
        class="mt-2 px-3 py-1 bg-red-200 dark:bg-red-800 text-red-700 dark:text-red-300 rounded hover:bg-red-300 dark:hover:bg-red-700 transition-colors"
      >
        {{ $t('BRANCHES.VIEW_OVERRIDES.TRY_AGAIN') }}
      </button>
    </div>

    <!-- Overrides List -->
    <div v-else>
      <div 
        v-if="overrideList.length !== 0" 
        v-for="(override, index) in newOverrideList" 
        :key="index" 
        class="flex flex-col gap-4 p-4 shadow-md rounded-lg bg-white dark:bg-slate-800 mb-4 transition-all duration-300 hover:shadow-lg"
      >
        <div class="flex justify-between items-center">
          <div class="flex flex-col gap-1 p-2 w-full">
            <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-100">
              {{ $t('BRANCHES.VIEW_OVERRIDES.DATE_LABEL') }} {{ override.date }}
            </h2>
            <p class="text-sm text-gray-600 dark:text-gray-300">
              {{ $t('BRANCHES.VIEW_OVERRIDES.REASON_LABEL') }} {{ override.reason || '-' }}
            </p>
            <div 
              v-for="(slot, idx) in override.slots" 
              :key="idx" 
              class="flex items-center justify-between w-full py-2 border-b border-gray-200 dark:border-gray-700 last:border-0"
            >
              <p class="text-sm text-gray-600 dark:text-gray-300">
                {{ $t('BRANCHES.VIEW_OVERRIDES.TIME_LABEL') }} {{ formatTime(slot.start_time) }} - {{ formatTime(slot.end_time) }}
              </p>
              <woot-button
                variant="clear"
                size="tiny"
                color-scheme="alert"
                icon="delete"
                @click="confirmingDeleteId = slot.id"
                :disabled="confirmingDeleteId === slot.id"
              >
                {{ confirmingDeleteId === slot.id ? $t('BRANCHES.VIEW_OVERRIDES.DELETING') : $t('BRANCHES.VIEW_OVERRIDES.DELETE_OVERRIDE') }}
              </woot-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div 
        v-if="overrideList.length === 0" 
        class="text-center bg-gray-50 dark:bg-slate-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p class="text-gray-600 dark:text-gray-300 mb-2">{{ $t('BRANCHES.VIEW_OVERRIDES.EMPTY_STATE.NO_OVERRIDES') }}</p>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          {{ $t('BRANCHES.VIEW_OVERRIDES.EMPTY_STATE.CREATE_PROMPT') }}
        </p>
      </div>
    </div>
  </div>
</template>
